"use client";
/**
 * Shared "Email Results" modal used across the calculators page.
 *
 * Any calc triggers the modal with a `summary` object describing its inputs +
 * outputs, plus a readable label. On Send, we compose a mailto: URL with a
 * plain-text body and the user's default email (from settings) as recipient.
 * No data leaves the browser — it's a client-side mailto only.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Mail, X } from "lucide-react";
import { useCalcSettings } from "./settings-context";

export type EmailSummary = {
  calcLabel: string;
  rows: { label: string; value: string }[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  summary: EmailSummary | null;
};

export function EmailResultsModal({ open, onClose, summary }: Props) {
  const { settings } = useCalcSettings();
  const [site, setSite] = useState("");
  const [job, setJob] = useState("");
  const [to, setTo] = useState(settings.defaultEmail);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Refresh default email when settings change + dialog opens.
  useEffect(() => {
    if (open) setTo(settings.defaultEmail);
  }, [open, settings.defaultEmail]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  const send = useCallback(() => {
    if (!summary) return;
    const lines: string[] = [];
    lines.push(`DRR — ${summary.calcLabel}`);
    if (site) lines.push(`Site: ${site}`);
    if (job) lines.push(`Job: ${job}`);
    lines.push("");
    for (const r of summary.rows) lines.push(`${r.label}: ${r.value}`);
    lines.push("");
    lines.push("Generated by Disaster Recovery Restoration — godrr.com/authority/calculators");
    const subject = `DRR ${summary.calcLabel}${job ? " — " + job : ""}`;
    const body = lines.join("\n");
    const href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
    onClose();
  }, [summary, site, job, to, onClose]);

  if (!open || !summary) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="email-modal-title"
      style={{ position: "fixed", inset: 0, zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem", background: "rgba(5,13,24,0.78)", backdropFilter: "blur(4px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={dialogRef}
        style={{ width: "100%", maxWidth: 520, background: "#ffffff", color: "#080F1A", borderRadius: 16, boxShadow: "0 24px 80px rgba(0,0,0,0.45)", overflow: "hidden" }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", background: "#B88A2F", color: "#080F1A" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Mail size={18} />
            <h2 id="email-modal-title" style={{ fontSize: 16, fontWeight: 700, letterSpacing: "0.02em" }}>Email Results — {summary.calcLabel}</h2>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{ width: 32, height: 32, border: "none", background: "transparent", cursor: "pointer", color: "#080F1A", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8 }}
          >
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="space-y-1.5">
            <Label htmlFor="em-to" style={{ color: "#080F1A" }}>Send to (email)</Label>
            <Input id="em-to" type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="name@company.com" style={{ color: "#080F1A", background: "#ffffff" }} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="em-site" style={{ color: "#080F1A" }}>Site description</Label>
            <Input id="em-site" value={site} onChange={(e) => setSite(e.target.value)} placeholder="e.g. 123 Main St — Tempe, AZ" style={{ color: "#080F1A", background: "#ffffff" }} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="em-job" style={{ color: "#080F1A" }}>Job name or #</Label>
            <Input id="em-job" value={job} onChange={(e) => setJob(e.target.value)} placeholder="e.g. JOB-2026-0412" style={{ color: "#080F1A", background: "#ffffff" }} />
          </div>
          <div style={{ background: "#f3f4f6", borderRadius: 8, padding: 12, marginTop: 4 }}>
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#6b7280", marginBottom: 6 }}>Summary preview</p>
            <dl style={{ fontSize: 13, display: "grid", gridTemplateColumns: "1fr auto", rowGap: 4, columnGap: 12 }}>
              {summary.rows.map((r) => (
                <div key={r.label} style={{ display: "contents" }}>
                  <dt style={{ color: "#374151" }}>{r.label}</dt>
                  <dd style={{ color: "#080F1A", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{r.value}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 6 }}>
            <Button variant="ghost" onClick={onClose} style={{ color: "#080F1A" }}>Cancel</Button>
            <Button onClick={send} disabled={!to} style={{ background: "#DC2626", color: "#ffffff" }}>
              <Mail className="mr-2 size-4" /> Send
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
